// Markers for the PLACES on a Leaflet map: the
// restaurant, the drop-off, the pickup and dropoff ends
// of a courier run.
//
// They were circles. A circle marks an AREA, and the
// drop-off marker at checkout is draggable: whatever the
// customer leaves it on is dispatched to the courier
// verbatim, so "which exact point" is the one question
// the marker has to answer. A teardrop answers it: its
// tip is a coordinate (CAL-457).
//
// The courier VEHICLE marker stays a circle, centred on
// its coordinate. It is a moving thing, not a place, and
// it lives in `courier_map_controller.js`.
import L from "leaflet";

// Shared, character for character, with the Ruby legend
// badge (`Svgs::MapPinFilledSvg`) so the pin printed
// beside "Delivering to" is the pin standing on the map.
// Change one and you must change the other.
//
// viewBox `0 0 32 40`: tip at (16, 40), head a true
// circle centred (16, 15) with radius 13 (bezier
// constant 0.5523 x 13 = 7.18).
export const PIN_PATH =
  "M16 2 C8.82 2 3 7.82 3 15 c0 10.4 13 25 13 25 " +
  "s13-14.6 13-25 C29 7.82 23.18 2 16 2 Z";

// Tailwind scans this file, but it only sees LITERAL
// class strings: a template literal like `text-${tone}`
// generates no CSS at all and the pin renders black. So
// every class a pin can wear is spelled out here.
//
// Colour is carried by `currentColor` on the path, set by
// these classes on the wrapper. NOT `var(--color-primary)`:
// this is daisyUI v4, whose variables are `--p` / `--pc`
// HSL triplets, so classes are the only safe route.
//
// `ring` is the drag affordance's stroke, and it is a
// property of the TONE because it has to contrast with
// that tone's own fill: see `dragRing`.
const TONES = {
  primary: {
    pin: "text-primary",
    letter: "text-primary-content",
    ring: "text-accent",
  },
  accent: {
    pin: "text-accent",
    letter: "text-accent-content",
    ring: "text-accent",
  },
  // Calisto's own accent came off the delivery CHECKOUT
  // in CAL-484 and off the ORDER pages in CAL-485, and
  // the drop-off pin was the one wearing it on both. Ink
  // is the page's own foreground, so it reads on any
  // merchant palette.
  //
  // Every drop-off marker we draw now asks for this
  // tone. `accent` above is kept as the role it always
  // was (the B / "Delivering to" end) rather than
  // deleted, because it is what an unbranded surface
  // should still reach for, but nothing in `app/` passes
  // it today: if you are adding a drop-off pin, the
  // question to answer is why yours is not ink.
  ink: {
    pin: "text-base-content",
    letter: "text-base-100",
    ring: "text-primary",
  },
};

// The tip IS the anchor: `iconAnchor.y` equals the icon
// height, so the point of the teardrop sits on the
// coordinate at every zoom level. The circles this
// replaced anchored at `[16, 32]`, the bottom edge of a
// circle, which is not a point on anything.
const ICON_SIZE = [32, 40];
const ICON_ANCHOR = [16, ICON_SIZE[1]];
const POPUP_ANCHOR = [0, -ICON_SIZE[1]];

// `drop-shadow-md`, never `shadow-lg`: a box-shadow on a
// teardrop draws a rectangle behind it. The CSS filter
// follows the alpha shape.
const SVG_CLASSES = "w-full h-full drop-shadow-md";

// The drag affordance, for the one marker the customer
// can move. A `ring-*` utility draws a RECTANGLE around
// the icon box (the one shape a teardrop is not), so the
// ring is a second copy of the pin outline, stroked wide
// and painted underneath.
//
// It is stroked in a SECOND colour rather than by
// recolouring the pin's own outline, and that colour
// comes from the tone (`TONES[tone].ring`) because it has
// to contrast with the fill it surrounds: a stroke in the
// pin's own colour is a ring you cannot see. The white
// 2px outline stays between the two, so the ring reads
// even where the pin and the ring are close in value.
// `overflow-visible` (draggable only) keeps the extra 3px
// from being clipped by the viewBox.
//
// Two pins are draggable and they do NOT share a fill:
// the admin venue editor's origin pin is `primary`, and
// the checkout's drop-off is `ink`. One hardcoded stroke
// cannot serve both, which is why this is per-tone rather
// than a constant.
function dragRing(ringClass) {
  return (
    `<path d="${PIN_PATH}" fill="none" ` +
    `class="${ringClass}" stroke="currentColor" ` +
    'stroke-width="8" stroke-linejoin="round"/>'
  );
}

// White outline so the pin reads against dark map tiles.
const PIN =
  `<path d="${PIN_PATH}" fill="currentColor" ` +
  'stroke="white" stroke-width="2" ' +
  'stroke-linejoin="round"/>';

function pinSvg(draggable, ringClass) {
  const classes = draggable
    ? `${SVG_CLASSES} overflow-visible`
    : SVG_CLASSES;

  return (
    `<svg viewBox="0 0 32 40" class="${classes}" ` +
    'aria-hidden="true">' +
    (draggable ? dragRing(ringClass) : "") +
    PIN +
    "</svg>"
  );
}

// An overlaid span rather than SVG `<text>`, positioned
// at the head's centre (15/40 = 37.5%) with a half-height
// shift, so it stays centred whatever size the pin is
// rendered at.
function pinLetter(letter, letterClass) {
  if (!letter) return "";

  return (
    '<span class="absolute inset-x-0 top-[37.5%] ' +
    "-translate-y-1/2 text-center text-sm font-bold " +
    `leading-none ${letterClass}">${letter}</span>`
  );
}

/**
 * A Leaflet `divIcon` shaped like a map pin.
 *
 * @param {string} letter - drawn on the head ("A", "B",
 *   "P", "D"); omit for a lone marker, where a label
 *   would be noise.
 * @param {"primary"|"accent"|"ink"} tone - primary for
 *   the pickup / "Delivering from" end; ink for the
 *   drop-off / "Delivering to" end on every surface that
 *   spends no accent, which since CAL-485 is all of them
 *   (the delivery checkout, the route map and the live
 *   courier map). `accent` is the same drop-off role in
 *   Calisto's own colour and has no caller left. No
 *   exceptions: the legend badges use the same three,
 *   via the Ruby twin.
 * @param {boolean} draggable - adds the tone's drag ring
 *   and the grab cursor.
 */
export function pinDivIcon({
  letter = "",
  tone = "primary",
  draggable = false,
}) {
  // Loudly, not with a fallback to primary. A silent
  // default paints a drop-off pin in the pickup colour
  // and leaves the legend beside it saying otherwise:
  // a wrong map that looks like a working one. The Ruby
  // twin does the same via `TONES.fetch`.
  const palette = TONES[tone];
  if (!palette) {
    throw new Error(
      `pinDivIcon: unknown tone "${tone}" ` +
        `(expected ${Object.keys(TONES).join(" or ")})`
    );
  }

  // `pointer-events-auto` is inherited from Leaflet's
  // own `.leaflet-marker-icon.leaflet-interactive`, but
  // it is spelled out here because a `pointer-events-none`
  // anywhere in this markup would take the drag away.
  const grab = draggable
    ? " cursor-grab pointer-events-auto"
    : "";

  // `w-full h-full` rather than `w-8 h-10`: Leaflet sets
  // the box from `iconSize` in px, and that stays the one
  // source of truth for how big the pin is.
  const html =
    '<span class="relative block w-full h-full ' +
    `${palette.pin}${grab}">` +
    pinSvg(draggable, palette.ring) +
    pinLetter(letter, palette.letter) +
    "</span>";

  return L.divIcon({
    html,
    iconSize: ICON_SIZE,
    iconAnchor: ICON_ANCHOR,
    popupAnchor: POPUP_ANCHOR,
    // Leaflet's default `leaflet-div-icon` paints a white
    // box with a border behind the pin.
    className: "",
  });
}
